import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneRight, Sparkle, CalendarCheck } from "@phosphor-icons/react";
import { scrollToId, openBooking } from "@/lib/anim";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionId = (() => {
  let s = typeof window !== "undefined" && localStorage.getItem("mobyle_chat_session");
  if (!s) {
    s = `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    if (typeof window !== "undefined") localStorage.setItem("mobyle_chat_session", s);
  }
  return s;
})();

const GREETING =
  "Hey — I'm Volt, Mobyle's AI build guide. ⚡ What would you like to build? A website, an app, an AI automation… or that idea you've been sitting on?";

const QUICK = [
  "A custom website",
  "An app for my business",
  "AI automations",
  "Not sure yet — help me",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const openHandler = () => {
      setOpen(true);
      setPulse(false);
    };
    window.addEventListener("open-mobyle-chat", openHandler);
    return () => window.removeEventListener("open-mobyle-chat", openHandler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Notify the hero robot when the chat opens/closes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mobyle-chat-toggle", { detail: { open } }));
  }, [open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const json = JSON.parse(data);
            if (json.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + json.delta,
                };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Hmm, my connection hiccuped. Try again, or just book a call and we'll take it from there!",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const bookCall = () => {
    setOpen(false);
    openBooking();
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 220, damping: 18 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => {
          setOpen((v) => !v);
          setPulse(false);
        }}
        className="fixed bottom-6 right-6 z-[60] h-16 w-16 rounded-full flex items-center justify-center text-black bg-gradient-to-tr from-teal via-cobalt to-violet shadow-[0_10px_40px_-6px_rgba(59,110,255,0.7)]"
        data-testid="chat-launcher"
        aria-label="Open AI chat"
      >
        {pulse && (
          <span className="absolute inset-0 rounded-full bg-teal/50 animate-ping" />
        )}
        <span className="relative">
          {open ? <X size={26} weight="bold" /> : <ChatCircleDots size={28} weight="fill" />}
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[76vh] rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a14]/95 backdrop-blur-2xl flex flex-col shadow-2xl"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="relative p-4 border-b border-white/10 bg-gradient-to-r from-teal/15 via-cobalt/15 to-violet/15">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full flex items-center justify-center bg-gradient-to-tr from-teal to-violet text-black">
                  <Sparkle size={20} weight="fill" />
                </div>
                <div>
                  <div className="font-heading font-bold text-white leading-none">Volt</div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-white/60">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal animate-pulse" />
                    Mobyle build guide · online
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-4 space-y-3"
              data-testid="chat-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-r from-cobalt to-violet text-white rounded-br-sm"
                        : "bg-white/5 border border-white/10 text-white/90 rounded-bl-sm"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal animate-bounce" />
                        <span className="h-1.5 w-1.5 rounded-full bg-cobalt animate-bounce" style={{ animationDelay: "0.15s" }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-violet animate-bounce" style={{ animationDelay: "0.3s" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      className="text-xs text-white/80 border border-white/15 rounded-full px-3 py-1.5 hover:border-teal hover:text-teal transition-colors"
                      data-testid={`chat-quick-${q.slice(0, 6).toLowerCase().replace(/\s/g, "")}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Book a call bar */}
            <button
              onClick={bookCall}
              className="mx-4 mb-2 inline-flex items-center justify-center gap-2 text-sm font-semibold text-black rounded-full py-2.5 bg-gradient-to-r from-teal to-cobalt hover:opacity-90 transition-opacity"
              data-testid="chat-book-call"
            >
              <CalendarCheck size={17} weight="fill" />
              Book a free build consult
            </button>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-white/10 flex items-center gap-2"
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Describe what you want to build…"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-teal transition-colors"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-black bg-gradient-to-tr from-teal to-cobalt disabled:opacity-40 transition-opacity"
                data-testid="chat-send"
                aria-label="Send"
              >
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
